import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AuthSessionService } from '@/core/services/auth-session.service';
import { ClientsTreeCoordinatorService, OpenClientConnectPayload } from '@/features/clients-tree/clients-tree-coordinator.service';
import {
  ChatMessageItem,
  ChatMessageQuoteRef,
  ClientConnectChatService,
  ClientConnectDialog,
} from '@/core/services/chat/client-connect-chat.service';
import { SupportChatDialog, SupportChatService } from '@/core/services/chat/support-chat.service';
import { MarketFeathersService } from '@/core/services/chat/market-feathers.service';
import { ClientChatStatsStore } from '@/core/services/chat/client-chat-stats-store.service';
import { NotificationsStore } from '@/core/services/notifications-store.service';
import { User } from '@/core/models/user';

const USER_TYPE_LABELS: Record<string, string> = {
  OWNER: 'Руководитель',
  MANAGER: 'Менеджер',
  EMPLOYEE: 'Продавец',
  AGENT: 'Торговый агент',
};

const KNOWN_PARTICIPANT_TYPES = new Set(['AGENT', 'OWNER', 'MANAGER', 'EMPLOYEE']);
const FORWARDED_PLACEHOLDERS = new Set(['«Пересланное сообщение»', '[Пересланное сообщение]']);

interface MessageContextMenuState {
  x: number;
  y: number;
  message: ChatMessageItem;
}

interface ChatListRow {
  clientId: number;
  clientName: string;
  clientInn: string;
  distributorId: number | null;
  user: User;
  lastMessage: string;
  lastMessageAt: string | null;
  unread: number;
}

@Component({
  selector: 'app-client-connect-sidebar',
  standalone: true,
  imports: [ReactiveFormsModule, DatePipe],
  templateUrl: './client-connect-sidebar.component.html',
  styleUrl: './client-connect-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientConnectSidebarComponent implements OnInit {
  private readonly coordinator = inject(ClientsTreeCoordinatorService);
  private readonly chatService = inject(ClientConnectChatService);
  private readonly supportChat = inject(SupportChatService);
  private readonly feathers = inject(MarketFeathersService);
  private readonly statsStore = inject(ClientChatStatsStore);
  private readonly notificationsStore = inject(NotificationsStore);
  private readonly authSession = inject(AuthSessionService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly messagesEl = viewChild<ElementRef<HTMLElement>>('messagesEl');
  private readonly notificationsEl = viewChild<ElementRef<HTMLElement>>('notificationsEl');

  readonly isOpen = signal(false);
  readonly viewMode = signal<'list' | 'dialog' | 'notifications'>('list');
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly messages = signal<ChatMessageItem[]>([]);
  readonly dialog = signal<ClientConnectDialog | SupportChatDialog | null>(null);
  readonly participantName = signal('');
  readonly infoTags = signal<string[]>([]);
  readonly contextMenu = signal<MessageContextMenuState | null>(null);
  readonly editingMessage = signal<ChatMessageItem | null>(null);
  readonly quotingMessage = signal<ChatMessageItem | null>(null);

  readonly isSupportDialog = computed(() => this.dialog()?.type === 'support');

  readonly supportPreview = computed(() => {
    this.statsStore.revision$();
    return this.statsStore.supportPreview();
  });

  readonly supportLastMessage = computed(() => {
    return this.supportPreview().lastMessage || 'Задайте вопрос - ответим быстро';
  });

  readonly notificationsPreview = computed(() => this.notificationsStore.preview());
  readonly notificationsFeed = computed(() => this.notificationsStore.feed());
  readonly notificationsLoading = computed(() => this.notificationsStore.loading());
  readonly notificationsLastMessage = computed(() => {
    return this.notificationsPreview().lastMessage || 'Системные уведомления';
  });
  readonly showNotificationsScrollBtn = signal(false);

  readonly listRows = computed(() => {
    this.statsStore.revision$();
    const rows: ChatListRow[] = [];
    for (const item of this.statsStore.conversationItems()) {
      for (const user of item.users || []) {
        rows.push({
          clientId: Number(item.clientId),
          clientName: item.name || '',
          clientInn: item.inn || '',
          distributorId: user.distributorId != null ? Number(user.distributorId) : null,
          user: {
            id: user.id,
            name: user.name,
            surname: user.surname,
            patronymic: user.patronymic,
            type: user.type,
          },
          lastMessage: user.lastMessage || (user.lastMessageImageUrl ? 'Изображение' : 'Нет сообщений'),
          lastMessageAt: user.lastMessageAt,
          unread: Number(user.unread) || 0,
        });
      }
    }
    return rows.sort((a, b) => {
      if (a.unread !== b.unread) {
        return b.unread - a.unread;
      }
      return (b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0) - (a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0);
    });
  });

  readonly messageControl = new FormControl('', { nonNullable: true });

  private unsubscribeRealtime: (() => void) | null = null;
  private currentUserId: number | null = null;
  private fromList = false;
  private notificationsReady = false;

  constructor() {
    effect(() => {
      if (this.authSession.isAuthenticated()) {
        this.statsStore.loadConversations();
      }
    });

    effect(() => {
      if (this.viewMode() !== 'notifications') {
        return;
      }
      this.notificationsStore.items();
      untracked(() => this.notificationsStore.markUnreadAsRead());
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeContextMenu();
  }

  ngOnInit(): void {
    const token = this.authSession.getToken();
    if (token) {
      this.feathers.authenticate(token).subscribe({
        next: (data) => {
          this.currentUserId = data?.user?.id ?? this.authSession.session()?.user?.id ?? null;
          this.statsStore.loadConversations();
        },
        error: () => undefined,
      });
    }

    this.coordinator.openChat$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      this.fromList = this.isOpen() && this.viewMode() === 'list';
      this.openFromPayload(payload);
    });

    this.coordinator.openChatList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.openList();
    });

    this.coordinator.toggleChatList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isOpen()) {
        this.close();
      } else {
        this.openList();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.unsubscribeRealtime?.();
      this.chatService.close();
    });
  }

  close(): void {
    this.leaveDialog();
    this.fromList = false;
    this.viewMode.set('list');
    this.isOpen.set(false);
  }

  goBack(): void {
    if (this.viewMode() === 'notifications') {
      this.showNotificationsScrollBtn.set(false);
      this.viewMode.set('list');
      return;
    }
    this.leaveDialog();
    if (this.fromList) {
      this.viewMode.set('list');
      this.statsStore.loadConversations();
      return;
    }
    this.isOpen.set(false);
  }

  openListConversation(row: ChatListRow): void {
    this.fromList = true;
    this.openFromPayload({
      client: {
        id: row.clientId,
        externalId: '',
        name: row.clientName,
        inn: row.clientInn,
        kpp: '',
        ogrn: '',
        formattedId: String(row.clientId),
        active: true,
        fields: [],
        enumFields: [],
        distributor: row.distributorId != null ? { id: row.distributorId } : undefined,
      },
      user: row.user,
    });
  }

  userFullName(user: User): string {
    return [user.surname, user.name, user.patronymic].filter(Boolean).join(' ') || 'Пользователь';
  }

  roleLabel(type?: string): string {
    const typeKey = String(type || '').toUpperCase();
    return USER_TYPE_LABELS[typeKey] || '';
  }

  formatListDate(value: string | null): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    if (d.getFullYear() === now.getFullYear()) {
      return `${dd}.${mm}`;
    }
    return `${dd}.${mm}.${d.getFullYear()}`;
  }

  openNotifications(): void {
    this.fromList = true;
    this.leaveDialog();
    this.notificationsReady = false;
    this.showNotificationsScrollBtn.set(false);
    this.isOpen.set(true);
    this.viewMode.set('notifications');
    this.notificationsStore.reload(() => this.scrollNotificationsToBottom());
  }

  onNotificationsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.showNotificationsScrollBtn.set(distanceFromBottom > 150);

    if (!this.notificationsReady || this.notificationsStore.loadingNext()) {
      return;
    }
    if (el.scrollTop >= 80) {
      return;
    }

    const prevHeight = el.scrollHeight;
    const prevTop = el.scrollTop;
    this.notificationsStore.loadNext(() => {
      el.scrollTop = prevTop + (el.scrollHeight - prevHeight);
    });
  }

  scrollNotificationsToBottom(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        const el = this.notificationsEl()?.nativeElement;
        if (el) {
          el.scrollTop = el.scrollHeight;
          this.notificationsReady = true;
          this.showNotificationsScrollBtn.set(false);
        }
      });
    });
  }

  openSupport(): void {
    this.fromList = true;
    this.leaveDialog();
    this.isOpen.set(true);
    this.loading.set(true);

    const token = this.authSession.getToken();
    const ensureAuth$ = token ? this.feathers.authenticate(token) : this.feathers.isAuthChat();

    ensureAuth$.subscribe({
      next: () => {
        this.currentUserId = this.authSession.session()?.user?.id ?? this.currentUserId;
        this.supportChat.getSupportUserId().subscribe({
          next: (supportUserId) => {
            const dialog: SupportChatDialog = { type: 'support', supportUserId };
            this.resetComposerState();
            this.closeContextMenu();
            this.supportChat.open(dialog);
            this.dialog.set(dialog);
            this.participantName.set('Чат с поддержкой IQMarket');
            this.infoTags.set([]);
            this.messages.set([]);
            this.viewMode.set('dialog');
            this.bindDialogMessages(this.supportChat, () => this.statsStore.refreshSupport());
          },
          error: () => {
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private openList(): void {
    this.fromList = true;
    this.leaveDialog();
    this.showNotificationsScrollBtn.set(false);
    this.viewMode.set('list');
    this.isOpen.set(true);
    this.statsStore.loadConversations();
    this.notificationsStore.loadInitial();
  }

  private leaveDialog(): void {
    const dialog = this.dialog();
    this.resetComposerState();
    this.closeContextMenu();
    this.unsubscribeRealtime?.();
    this.unsubscribeRealtime = null;
    this.chatService.close();
    this.supportChat.close();
    this.dialog.set(null);
    this.messages.set([]);
    this.infoTags.set([]);
    this.participantName.set('');
    this.messageControl.setValue('');
    if (dialog?.type === 'support') {
      this.statsStore.refreshSupport();
    } else if (dialog?.type === 'client-connect') {
      this.statsStore.refreshClient(Number(dialog.typeId), dialog.distributorId);
    }
  }

  send(): void {
    const text = this.messageControl.value.trim();
    const editing = this.editingMessage();
    const quoting = this.quotingMessage();

    if (this.sending()) {
      return;
    }

    if (editing) {
      if (!text) {
        return;
      }
      this.sending.set(true);
      this.chatApi().editMessage(editing.id, text, editing.attached).subscribe({
        next: (msg) => {
          this.replaceMessage(msg);
          this.messageControl.setValue('');
          this.editingMessage.set(null);
          this.sending.set(false);
          this.refreshStats();
        },
        error: () => {
          this.sending.set(false);
        },
      });
      return;
    }

    if (!text && !quoting) {
      return;
    }

    this.sending.set(true);
    this.chatApi().sendMessage(text, quoting).subscribe({
      next: (msg) => {
        this.appendMessage(msg);
        this.messageControl.setValue('');
        this.quotingMessage.set(null);
        this.sending.set(false);
        this.scrollToBottom();
        this.refreshStats();
      },
      error: () => {
        this.sending.set(false);
      },
    });
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  canSend(): boolean {
    if (this.sending()) {
      return false;
    }
    const text = this.messageControl.value.trim();
    if (this.editingMessage()) {
      return !!text;
    }
    return !!text || !!this.quotingMessage();
  }

  onMessageContextMenu(event: MouseEvent, message: ChatMessageItem): void {
    event.preventDefault();
    event.stopPropagation();

    const menuWidth = 227;
    const itemCount =
      (this.canEdit(message) ? 1 : 0) +
      (message?.text ? 1 : 0) +
      (this.canQuote(message) ? 1 : 0) +
      (this.canDelete(message) ? 1 : 0);

    if (!itemCount) {
      return;
    }

    const estimatedHeight = itemCount * 36;
    const padding = 8;
    const maxX = window.innerWidth - menuWidth - padding;
    const maxY = window.innerHeight - estimatedHeight - padding;

    this.contextMenu.set({
      x: Math.max(padding, Math.min(event.clientX, maxX)),
      y: Math.max(padding, Math.min(event.clientY, maxY)),
      message,
    });
  }

  closeContextMenu(): void {
    this.contextMenu.set(null);
  }

  canEdit(message: ChatMessageItem): boolean {
    return this.isOwnMessage(message) && !this.isSystemMessage(message);
  }

  canQuote(message: ChatMessageItem): boolean {
    return !this.isSystemMessage(message);
  }

  canDelete(message: ChatMessageItem): boolean {
    return this.isOwnMessage(message) && !this.isSystemMessage(message);
  }

  startEdit(message: ChatMessageItem): void {
    this.closeContextMenu();
    this.quotingMessage.set(null);
    this.editingMessage.set(message);
    this.messageControl.setValue(message.text || '');
  }

  cancelEdit(): void {
    this.editingMessage.set(null);
    this.messageControl.setValue('');
  }

  startQuote(message: ChatMessageItem): void {
    this.closeContextMenu();
    this.editingMessage.set(null);
    this.quotingMessage.set(message);
    this.messageControl.setValue('');
  }

  cancelQuote(): void {
    this.quotingMessage.set(null);
  }

  async copyMessage(message: ChatMessageItem): Promise<void> {
    const text = message?.text;
    if (!text) {
      this.closeContextMenu();
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const inp = document.createElement('textarea');
        inp.value = text;
        inp.setAttribute('readonly', '');
        inp.style.position = 'fixed';
        inp.style.left = '-9999px';
        document.body.appendChild(inp);
        inp.select();
        document.execCommand('copy');
        inp.remove();
      }
    } catch {
      /* ignore copy errors */
    }

    this.closeContextMenu();
  }

  removeMessage(message: ChatMessageItem): void {
    if (!message?.id || !this.canDelete(message)) {
      this.closeContextMenu();
      return;
    }

    this.chatApi().removeMessage(message.id).subscribe({
      next: () => {
        this.messages.set(this.messages().filter((item) => item.id !== message.id));
        if (this.editingMessage()?.id === message.id) {
          this.cancelEdit();
        }
        if (this.quotingMessage()?.id === message.id) {
          this.cancelQuote();
        }
        this.closeContextMenu();
        this.refreshStats();
      },
      error: () => {
        this.closeContextMenu();
      },
    });
  }

  isOwnMessage(msg: ChatMessageItem): boolean {
    if (msg?.senderAddressId) {
      return true;
    }
    const userId = msg.userId ?? msg.user?.id;
    return this.currentUserId != null && Number(userId) === Number(this.currentUserId);
  }

  isSystemMessage(message: ChatMessageItem): boolean {
    return !!message?.isSystem || !!message?.isTicketInfo;
  }

  ticketLabel(msg: ChatMessageItem): string | null {
    if (!msg?.isTicketInfo) {
      return null;
    }
    const id = msg.ticket?.id ?? msg.ticketId;
    if (id == null || id === '') {
      return null;
    }
    const key = msg.ticket?.youtrackIssueKey;
    return key ? `Обращение #${id} (${key})` : `Обращение #${id}`;
  }

  messageAuthor(msg: ChatMessageItem | null | undefined): string {
    const user = msg?.user;
    if (!user) {
      return '';
    }
    return [user.surname, user.name].filter(Boolean).join(' ');
  }

  quoteAuthor(quote: ChatMessageQuoteRef): string {
    const user = quote?.message?.user;
    if (!user) {
      return '';
    }
    const initial = user.name ? `${user.name.charAt(0).toUpperCase()}.` : '';
    return [user.surname, initial].filter(Boolean).join(' ');
  }

  messageQuotes(msg: ChatMessageItem): ChatMessageQuoteRef[] {
    return Array.isArray(msg?.attached?.messages) ? msg.attached!.messages! : [];
  }

  displayText(msg: ChatMessageItem): string {
    const text = msg?.text || '';
    if (FORWARDED_PLACEHOLDERS.has(text)) {
      return '';
    }
    return text;
  }

  scrollToQuoted(quote: ChatMessageQuoteRef): void {
    const linkId = quote?.linkId ?? quote?.message?.id;
    if (linkId == null) {
      return;
    }
    const el = this.messagesEl()?.nativeElement?.querySelector(`#chat-msg-${linkId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private openFromPayload(payload: OpenClientConnectPayload): void {
    const typeId = Number(payload.client.id);
    const extraTypeId = Number(payload.user.id);
    const distributorId = this.resolveDistributorId(payload);

    if (!typeId || Number.isNaN(typeId)) {
      console.error('[client-connect] missing market client id (typeId)');
      return;
    }
    if (!extraTypeId || Number.isNaN(extraTypeId)) {
      console.error('[client-connect] missing user id (extraTypeId)');
      return;
    }

    const dialog: ClientConnectDialog = {
      type: 'client-connect',
      typeId,
      extraTypeId,
      distributorId,
      clientName: payload.client.name,
      clientInn: payload.client.inn,
    };

    this.resetComposerState();
    this.closeContextMenu();
    this.unsubscribeRealtime?.();
    this.supportChat.close();
    this.chatService.open(dialog);
    this.dialog.set(dialog);
    this.viewMode.set('dialog');
    this.participantName.set(
      [payload.user.surname, payload.user.name, payload.user.patronymic].filter(Boolean).join(' ') ||
        payload.user.username ||
        'Пользователь'
    );
    this.infoTags.set(this.buildInfoTags(payload));
    this.isOpen.set(true);
    this.loading.set(true);
    this.messages.set([]);

    const token = this.authSession.getToken();
    const ensureAuth$ = token ? this.feathers.authenticate(token) : this.feathers.isAuthChat();

    ensureAuth$.subscribe({
      next: () => {
        this.currentUserId = this.authSession.session()?.user?.id ?? this.currentUserId;
        this.bindDialogMessages(this.chatService, () => this.statsStore.refreshClient(typeId, distributorId));
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private resolveDistributorId(payload: OpenClientConnectPayload): number | undefined {
    const candidates: unknown[] = [
      payload.client.distributor?.id,
      (payload.user as { distributorId?: number }).distributorId,
      payload.outlet?.client?.distributor?.id,
      payload.outlet?.distributorId,
    ];

    for (const value of candidates) {
      const id = Number(value);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }

    return undefined;
  }

  private buildInfoTags(payload: OpenClientConnectPayload): string[] {
    const tags: string[] = [];

    if (payload.client.name) {
      tags.push(String(payload.client.name));
    }

    if (payload.client.id != null) {
      tags.push(`ID: ${payload.client.id}`);
    }

    if (payload.client.inn) {
      tags.push(`ИНН: ${payload.client.inn}`);
    }

    const typeKey = String(payload.user.type || '').toUpperCase();
    if (KNOWN_PARTICIPANT_TYPES.has(typeKey)) {
      tags.push(USER_TYPE_LABELS[typeKey] ?? typeKey);
    }

    return tags;
  }

  private appendMessage(msg: ChatMessageItem): void {
    if (!msg?.id) {
      return;
    }
    if (this.messages().some((item) => item.id === msg.id)) {
      return;
    }
    this.messages.set([...this.messages(), msg]);
  }

  private replaceMessage(msg: ChatMessageItem): void {
    if (!msg?.id) {
      return;
    }
    const list = this.messages();
    const index = list.findIndex((item) => item.id === msg.id);
    if (index < 0) {
      return;
    }
    const next = [...list];
    next[index] = { ...list[index], ...msg };
    this.messages.set(next);
  }

  private refreshStats(): void {
    const dialog = this.dialog();
    if (dialog?.type === 'support') {
      this.statsStore.refreshSupport();
      return;
    }
    if (dialog?.type === 'client-connect') {
      this.statsStore.refreshClient(Number(dialog.typeId), dialog.distributorId);
    }
  }

  private chatApi(): ClientConnectChatService | SupportChatService {
    return this.dialog()?.type === 'support' ? this.supportChat : this.chatService;
  }

  private bindDialogMessages(
    api: ClientConnectChatService | SupportChatService,
    onChanged: () => void
  ): void {
    api.getMessages(0, 50).subscribe({
      next: (items) => {
        this.messages.set(items);
        this.loading.set(false);
        this.scrollToBottom();
        this.markSupportMessagesRead(items);
        onChanged();
        const unsubCreated = api.onMessageCreated((msg) => {
          this.appendMessage(msg);
          this.scrollToBottom();
          if (this.dialog()?.type === 'support' && this.supportChat.isUnreadIncoming(msg, this.currentUserId)) {
            this.supportChat.markRead(msg.id).subscribe();
          }
          onChanged();
        });
        const unsubRemoved = api.onMessageRemoved((msg) => {
          if (msg?.id == null) {
            return;
          }
          this.messages.set(this.messages().filter((item) => item.id !== msg.id));
          onChanged();
        });
        const unsubPatched = api.onMessagePatched((msg) => {
          if (msg?.id == null) {
            return;
          }
          this.replaceMessage(msg);
        });
        this.unsubscribeRealtime = () => {
          unsubCreated();
          unsubRemoved();
          unsubPatched();
        };
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  private markSupportMessagesRead(items: ChatMessageItem[]): void {
    if (this.dialog()?.type !== 'support') {
      return;
    }
    for (const msg of items) {
      if (msg?.id && this.supportChat.isUnreadIncoming(msg, this.currentUserId)) {
        this.supportChat.markRead(msg.id).subscribe();
      }
    }
  }

  private resetComposerState(): void {
    this.editingMessage.set(null);
    this.quotingMessage.set(null);
  }

  private scrollToBottom(): void {
    queueMicrotask(() => {
      const el = this.messagesEl()?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
